/**
 * Home, chapter 01 — "Every product has two sides".
 *
 * One pinned sequence: two sides → the halves part → the hero rises and
 * draws himself → his name is typed → the halves close on him. The chapter's
 * pinned length, in screen heights, is `data-split-pin`. The markup is the
 * server's (templates/site/sections/home/opening.html.twig).
 *
 * @module sections/home/opening
 */

import { REDUCED_MOTION } from '../../config.js';
import { ScrollTrigger, gsap } from '../../lib/gsap.js';
import { tuckHeader } from '../../chrome/header.js';

/**
 * @param {HTMLElement} root
 */
export function animate(root) {
    const frontHalf = root.querySelector('[data-half="front"]');
    const backHalf = root.querySelector('[data-half="back"]');
    const hero = root.querySelector('[data-hero]');
    const wireframe = root.querySelectorAll('.hoodie__stroke');
    const name = root.querySelector('[data-name]');
    const nameText = root.querySelector('[data-name-text]');
    const fullName = nameText?.textContent ?? '';
    const typed = { length: 0 };

    const captions = (role) => root.querySelectorAll(`[data-cap="${role}"]`);

    // Upright screens open all the way: there the hero is as wide as the gap.
    const spread = (side) => () => side * (window.innerWidth < window.innerHeight ? 100 : 62);

    const timeline = gsap
        .timeline({ paused: true, defaults: { ease: 'none' } })
        .to(root.querySelector('[data-split-hint]'), { opacity: 0, duration: 0.05 }, 0.02)
        .to(root.querySelector('[data-banner]'), { opacity: 0, scale: 0.85, duration: 0.1 }, 0.08)
        .to(captions('open'), { opacity: 0, y: -20, duration: 0.08 }, 0.12)
        .fromTo(frontHalf, { xPercent: 0 }, { xPercent: spread(-1), duration: 0.22, ease: 'power2.inOut' }, 0.18)
        .fromTo(backHalf, { xPercent: 0 }, { xPercent: spread(1), duration: 0.22, ease: 'power2.inOut' }, 0.18)
        .fromTo(hero, { yPercent: 105 }, { yPercent: 0, duration: 0.2, ease: 'power2.out' }, 0.24)
        .fromTo(captions('pick'), { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.04 }, 0.38)
        .to(captions('pick'), { opacity: 0, y: -16, duration: 0.03 }, 0.51)
        .fromTo(wireframe, { drawSVG: '0%' }, { drawSVG: '100%', duration: 0.3 }, 0.4)
        .fromTo(captions('hood'), { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.04 }, 0.55)
        .to(captions('hood'), { opacity: 0, y: -16, duration: 0.03 }, 0.71)
        .fromTo(name, { opacity: 0 }, { opacity: 1, duration: 0.02 }, 0.57)
        .fromTo(
            typed,
            { length: 0 },
            {
                length: fullName.length,
                duration: 0.13,
                onUpdate: () => {
                    nameText.textContent = fullName.slice(0, Math.round(typed.length));
                },
            },
            0.58,
        )
        .to(frontHalf, { xPercent: 0, duration: 0.18, ease: 'power2.inOut' }, 0.74)
        .to(backHalf, { xPercent: 0, duration: 0.18, ease: 'power2.inOut' }, 0.74)
        .fromTo(captions('close'), { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.06 }, 0.86)
        .to({}, { duration: 0.06 }, 0.94);

    // Reduced motion: no pin, the closing frame (hero, name, both sides).
    if (REDUCED_MOTION) {
        timeline.progress(1);
        nameText.textContent = fullName;
        return;
    }

    nameText.textContent = '';

    const length = Number.parseFloat(root.dataset.splitPin) || 3;

    ScrollTrigger.create({
        trigger: root,
        pin: true,
        start: 'top top',
        end: () => `+=${Math.round(length * window.innerHeight)}`,
        scrub: true,
        animation: timeline,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => tuckHeader(self.progress > 0.06 && self.progress < 0.96),
        onLeave: () => tuckHeader(false),
        onLeaveBack: () => tuckHeader(false),
    });
}
